import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api, resolveImg } from "@/lib/adminApi";
import ProductBadges from "@/components/jd/ProductBadges";

const CAT_LABEL = { whiskey: "Whiskey", spirits: "Other Spirits" };

export default function ReviewPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    api.get(`/picks/item/${slug}`)
      .then((res) => { setItem(res.data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading")
    return <div className="min-h-screen flex items-center justify-center bg-bg text-bone eyebrow grain vignette">Pouring the review…</div>;

  if (status === "notfound")
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-bg text-bone grain vignette px-6 text-center">
        <h1 className="font-display text-5xl">Not on the shelf.</h1>
        <button onClick={() => navigate("/")} className="btn-outline">Back Home</button>
      </div>
    );

  const scoreVal = item.score == null ? null : (Number(item.score) % 1 === 0 ? Number(item.score).toFixed(0) : Number(item.score));
  const meta = [
    item.price && `Price: ${item.price}`,
    item.issue && `Issue: ${item.issue}`,
    item.reviewer && `Reviewer: ${item.reviewer}`,
    item.review_category && `Category: ${item.review_category}`,
  ].filter(Boolean);
  const paras = (item.review_body || "").split("\n").map((p) => p.trim()).filter(Boolean);

  return (
    <div className="grain vignette bg-bg text-bone min-h-screen">
      <header className="border-b sticky top-0 z-30 nav-blur" style={{ borderColor: "var(--hairline)" }}>
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="review-logo">
            <svg width="26" height="26" viewBox="0 0 40 40" fill="none">
              <path d="M8 8 L20 32 L32 8" stroke="var(--brass)" strokeWidth="1.4" />
              <circle cx="20" cy="20" r="3" fill="var(--amber)" />
            </svg>
            <span className="font-display text-[1.1rem] tracking-wide">Johnny Dram</span>
          </Link>
          <Link to={`/shelf/${item.category}`} className="btn-outline" data-testid="review-back">
            <ArrowLeft size={16} /> {CAT_LABEL[item.category] || "Top Shelf"}
          </Link>
        </div>
      </header>

      <article className="max-w-[860px] mx-auto px-6 sm:px-10 py-16 md:py-20">
        {/* Bottle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="flex justify-center mb-12"
        >
          <div className="relative w-[280px] max-w-full tile-frame" style={{ aspectRatio: "4/5" }}>
            <img src={resolveImg(item.image_url)} alt={item.name} className="absolute inset-0 w-full h-full object-cover" />
            <ProductBadges item={item} size={58} />
          </div>
        </motion.div>

        {/* Score + title */}
        <div className="grid grid-cols-12 gap-6 items-start">
          {scoreVal != null && (
            <div className="col-span-3 sm:col-span-2 text-center pr-4" style={{ borderRight: "2px solid var(--amber)" }} data-testid="review-score">
              <div className="font-display leading-none" style={{ color: "var(--amber)", fontSize: "clamp(3rem, 9vw, 5rem)" }}>{scoreVal}</div>
              <div className="eyebrow mt-2">Score</div>
              <div className="eyebrow" style={{ color: "var(--bone-dim)" }}>/10</div>
            </div>
          )}
          <div className={scoreVal != null ? "col-span-9 sm:col-span-10" : "col-span-12"}>
            <h1 className="font-display leading-[1.02] tracking-tight text-bone" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)" }} data-testid="review-title">
              {item.name}{item.abv ? `, ${item.abv}` : ""}
            </h1>
          </div>
        </div>

        {/* Meta line */}
        {meta.length > 0 && (
          <div className="mt-8 pt-6 pb-2 border-t flex flex-wrap items-center gap-x-3 gap-y-2" style={{ borderColor: "var(--hairline)" }} data-testid="review-meta">
            {meta.map((m, i) => (
              <span key={i} className="font-accent text-sm" style={{ letterSpacing: "0.06em", color: "var(--bone-dim)" }}>
                {m}{i < meta.length - 1 && <span className="ml-3" style={{ color: "var(--brass)" }}>•</span>}
              </span>
            ))}
          </div>
        )}

        {/* Review body */}
        <div className="mt-8 jd-prose" data-testid="review-body">
          {paras.length > 0
            ? paras.map((p, i) => <p key={i}>{p}</p>)
            : <p className="text-bone-dim italic">Tasting notes coming soon.</p>}
        </div>

        {/* Buy */}
        {item.affiliate_url && (
          <div className="mt-12 pt-8 border-t flex items-center justify-between flex-wrap gap-4" style={{ borderColor: "var(--hairline)" }}>
            <span className="font-display italic text-xl text-bone-dim">Bring it home.</span>
            <a href={item.affiliate_url} target="_blank" rel="noopener noreferrer sponsored" className="btn-solid" data-testid="review-buy">
              Find on Amazon <ArrowUpRight size={16} />
            </a>
          </div>
        )}
      </article>
    </div>
  );
}
